<template>
  <transition name="auth-fade">
    <div v-if="visible" class="back-confirm-overlay" @click.self="handleClose">
      <transition name="auth-zoom">
        <div class="back-confirm-modal">
          <div class="back-confirm-blur"></div>
          <div class="back-confirm-card turnstile-card">
            <!-- 标题 -->
            <div class="turnstile-header">
              <h2 class="turnstile-title">人机验证</h2>
              <p class="turnstile-description">请完成以下验证以继续注册</p>
            </div>
            
            <!-- Turnstile 验证组件 -->
            <div class="turnstile-content">
              <Turnstile
                :site-key="turnstileSiteKey"
                theme="light"
                size="normal"
                @success="onTurnstileSuccess"
                @error="onTurnstileError"
                @expired="onTurnstileExpired"
                @timeout="onTurnstileTimeout"
                ref="turnstileRef"
              />
            </div>
            
            <!-- 错误消息 -->
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            
            <!-- 底部按钮 -->
            <div class="turnstile-actions">
              <button 
                class="cancel-button" 
                @click="handleClose"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import Turnstile from '@/components/Turnstile.vue'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// Turnstile 相关
const turnstileSiteKey = '0x4AAAAAAB30C7VaDwD8MEfx'
const turnstileRef = ref(null)
const errorMessage = ref('')

// Turnstile 事件处理
const onTurnstileSuccess = (token) => {
  console.log('Turnstile 验证成功:', token)
  errorMessage.value = ''
  emit('success', token)
  handleClose()
}

const onTurnstileError = (errorCode) => {
  console.error('Turnstile 验证错误:', errorCode)
  errorMessage.value = '验证失败，请重试'
  resetTurnstile()
}

const onTurnstileExpired = () => {
  console.log('Turnstile 验证过期')
  errorMessage.value = '验证已过期，请重新验证'
  resetTurnstile()
}

const onTurnstileTimeout = () => {
  console.log('Turnstile 验证超时')
  errorMessage.value = '验证超时，请重试'
  resetTurnstile()
}

// 重置 Turnstile
const resetTurnstile = () => {
  if (turnstileRef.value) {
    turnstileRef.value.reset()
  }
}

// 关闭弹窗
const handleClose = () => {
  errorMessage.value = ''
  emit('close')
}

// 监听 ESC 关闭
const onKeydown = (e) => {
  if (e.key === 'Escape' && props.visible) {
    handleClose()
  }
}

// 监听 visible 变化，重置状态
watch(() => props.visible, (newVal) => {
  if (newVal) {
    errorMessage.value = ''
    // 弹窗打开时重置 Turnstile
    setTimeout(() => {
      resetTurnstile()
    }, 100)
  }
})

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
})

// 暴露方法供父组件调用
defineExpose({
  reset: resetTurnstile
})
</script>

<style scoped>
/* 复用 AuthModal 的毛玻璃遮罩与卡片视觉 */
.back-confirm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 15000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(10px);
}

.back-confirm-modal {
  position: relative;
  display: inline-block;
  width: 380px;
  max-width: 92vw;
  height: auto;
  padding: 0;
  border-radius: 35px;
  overflow: hidden;
  isolation: isolate;
  background-color: rgba(0,0,0,0.18);
}

.back-confirm-blur {
  position: absolute;
  inset: -40px;
  background: rgba(245, 245, 245, 0.6);
  background-blend-mode: normal, color-dodge;
  filter: blur(20px);
  backdrop-filter: blur(40px);
  border-radius: 34px;
  z-index: 0;
}

.back-confirm-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 35px;
  padding: 24px 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

/* Turnstile 卡片特定样式 */
.turnstile-card {
  min-height: 280px;
}

/* 标题区域 */
.turnstile-header {
  text-align: center;
  margin-bottom: 8px;
}

.turnstile-title {
  font-size: 24px;
  font-weight: 700;
  color: #000;
  margin: 0 0 8px 0;
  letter-spacing: -0.02em;
}

.turnstile-description {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
  font-weight: 500;
}

/* Turnstile 内容区域 */
.turnstile-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  min-height: 120px;
  width: 100%;
  overflow: hidden;
}

/* 限制 Turnstile 组件的宽度 */
.turnstile-content :deep(.turnstile-container) {
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  overflow: hidden;
}

.turnstile-content :deep(.turnstile-widget) {
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  transform-origin: center;
}

/* 如果 Turnstile 太宽，进行缩放 */
.turnstile-content :deep(.turnstile-widget > div) {
  max-width: 100%;
}

@media (max-width: 380px) {
  .turnstile-content :deep(.turnstile-widget) {
    transform: scale(0.85);
  }
}

/* 错误消息 */
.error-message {
  color: #ff6b6b;
  font-size: 0.875rem;
  text-align: center;
  padding: 0.75rem;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  margin: 0;
}

/* 底部按钮区域 */
.turnstile-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
}

.cancel-button {
  padding: 12px 32px;
  background: rgba(0, 0, 0, 0.06);
  color: #000;
  font-weight: 600;
  font-size: 15px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.cancel-button:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.cancel-button:active {
  transform: translateY(0);
  background: rgba(0, 0, 0, 0.08);
}

/* 弹层淡入淡出 */
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition: opacity 0.18s ease;
}

.auth-fade-enter-from,
.auth-fade-leave-to {
  opacity: 0;
}

/* 卡片轻微缩放与上浮 */
.auth-zoom-enter-active,
.auth-zoom-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
  will-change: transform, opacity;
}

.auth-zoom-enter-from,
.auth-zoom-leave-to {
  transform: translateY(6px) scale(0.98);
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .back-confirm-modal {
    width: 340px;
    max-width: 90vw;
  }
  
  .turnstile-title {
    font-size: 20px;
  }
  
  .turnstile-description {
    font-size: 13px;
  }
  
  .back-confirm-card {
    padding: 20px 16px;
  }
  
  .turnstile-content :deep(.turnstile-widget) {
    transform: scale(0.9);
  }
}

@media (max-width: 360px) {
  .back-confirm-modal {
    width: 300px;
  }
  
  .turnstile-content :deep(.turnstile-widget) {
    transform: scale(0.8);
  }
}
</style>
